/**
 * GameCreationWizard.js
 * Multi-step wizard for creating games
 * Step 1: Select Game Type
 * Step 2: Configure Game Settings
 * Step 3: Add & Configure Players
 * Step 4: Review & Start
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Stepper,
    Step,
    StepLabel,
    Button,
    Typography,
    CircularProgress,
    Alert
} from '@mui/material';
import GameTypeSelector from './GameTypeSelector';
import GameConfigForm from './GameConfigForm';
import PlayerConfiguration from './PlayerConfiguration';
import GameReview from './GameReview';
import gameManagementApi from './gameManagementApi';

const steps = ['Select Game Type', 'Configure Game', 'Add Players', 'Review & Start'];

// Default models for the first 8 players (in order) - SOTA from each company
// After the 8th player, additional players will cycle through this list
const DEFAULT_MODEL_SEQUENCE = [
    // Free-tier defaults only — premium (🔒) models become selectable for a fee later
    'google/gemini-3-flash-preview',        // 1. Google - Gemini 3 Flash // 🤖💸 10-01 agent floor (R10): was x-ai/grok-4.3 (premium)
    'anthropic/claude-haiku-4.5',            // 2. Anthropic - Haiku 4.5 (fast, cost-effective)
    'google/gemini-2.5-flash',              // 3. Google - Gemini Flash
    'openai/gpt-5-mini',                    // 4. OpenAI - GPT 5 Mini
    'deepseek/deepseek-v3.2',      // 5. DeepSeek - V3.2 (GPT-5 class, Dec 2025)
    'qwen/qwen3-235b-a22b-2507',            // 6. Alibaba - Qwen3 235B (multilingual SOTA)
    'moonshotai/kimi-k2-0905',              // 7. Moonshot - Kimi K2 (best open model)
    'meta-llama/llama-4-maverick'           // 8. Meta - Llama 4 Maverick (400B, 2nd on LM Arena)
];

// Get model for a player based on their position
const getModelForPlayer = (playerIndex) => {
    return DEFAULT_MODEL_SEQUENCE[playerIndex % DEFAULT_MODEL_SEQUENCE.length]
};

export default function GameCreationWizard({ onGameCreated }) {
    const [activeStep, setActiveStep] = useState(0);
    const [gameTypes, setGameTypes] = useState([]);
    const [selectedGameType, setSelectedGameType] = useState(null);
    const [gameTypeDetails, setGameTypeDetails] = useState(null);
    const [gameConfig, setGameConfig] = useState({});
    const [gameName, setGameName] = useState('');
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Load game types on mount
    useEffect(() => {
        loadGameTypes();
    }, []);

    const loadGameTypes = async () => {
        try {
            setLoading(true);
            const result = await gameManagementApi.getGameTypes();
            if (result.success) {
                setGameTypes(result.gameTypes);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
        setSelectedGameType(null);
        setGameTypeDetails(null);
        setGameConfig({});
        setGameName('');
        setPlayers([]);
        setError(null);
    };

    const handleGameTypeSelect = async (gameType) => {
        try {
            setLoading(true);
            setError(null);

            // Load detailed schema for this game type
            const result = await gameManagementApi.getGameTypeDetails(gameType.id);

            if (result.success) {
                setSelectedGameType(gameType);
                setGameTypeDetails(result.details);

                // Initialize with default config
                const defaultConfig = {};
                Object.keys(result.details.configSchema).forEach(key => {
                    const field = result.details.configSchema[key];
                    defaultConfig[key] = field.default;
                });
                setGameConfig(defaultConfig);

                // Initialize with recommended number of default AI players
                const defaultPlayers = [];
                for (let i = 0; i < gameType.recommendedPlayers; i++) {
                    defaultPlayers.push({
                        id: `player-${i}`,
                        name: `Player ${i + 1}`,
                        is_human: false,
                        model: getModelForPlayer(i), // Use sequential model selection
                        ...Object.keys(result.details.playerSchema)
                            .filter(key => key !== 'model') // Exclude model from defaults to preserve sequential value
                            .reduce((acc, key) => {
                                if (result.details.playerSchema[key].default !== undefined) {
                                    acc[key] = result.details.playerSchema[key].default;
                                }
                                return acc;
                            }, {})
                    });
                }
                setPlayers(defaultPlayers);

                handleNext();
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleConfigChange = (newConfig, newGameName) => {
        setGameConfig(newConfig);
        if (newGameName !== undefined) {
            setGameName(newGameName);
        }
    };

    const handlePlayersChange = (newPlayers) => {
        setPlayers(newPlayers);
    };

    const handleCreateGame = async () => {
        try {
            setLoading(true);
            setError(null);

            const result = await gameManagementApi.createGame({
                game_type: selectedGameType.id,
                game_name: gameName || `${selectedGameType.name} ${Date.now()}`,
                config: gameConfig,
                players: players
            });

            if (result.success) {
                handleNext(); // Move to success step
                if (onGameCreated) {
                    onGameCreated(result);
                }
            } else {
                setError(result.error || 'Failed to create game');
                if (result.validationErrors) {
                    setError(result.validationErrors.join(', '));
                }
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const isStepValid = () => {
        switch (activeStep) {
            case 0:
                return selectedGameType !== null;
            case 1:
                return gameName.trim() !== '';
            case 2:
                return players.length >= (gameTypeDetails?.minPlayers || 1);
            case 3:
                return true;
            default:
                return false;
        }
    };

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <GameTypeSelector
                        gameTypes={gameTypes}
                        onSelect={handleGameTypeSelect}
                        loading={loading}
                    />
                );
            case 1:
                return gameTypeDetails && (
                    <GameConfigForm
                        gameTypeDetails={gameTypeDetails}
                        config={gameConfig}
                        gameName={gameName}
                        onChange={handleConfigChange}
                    />
                );
            case 2:
                return gameTypeDetails && (
                    <PlayerConfiguration
                        gameTypeDetails={gameTypeDetails}
                        players={players}
                        onChange={handlePlayersChange}
                    />
                );
            case 3:
                return (
                    <GameReview
                        gameType={selectedGameType}
                        gameName={gameName}
                        config={gameConfig}
                        players={players}
                    />
                );
            default:
                return 'Unknown step';
        }
    };

    if (activeStep === steps.length) {
        return (
            <Box sx={{ mt: 2, mb: 1 }}>
                <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="h6">Game Created Successfully!</Typography>
                    <Typography>Your game has been created and is ready to play.</Typography>
                </Alert>
                <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                    <Box sx={{ flex: '1 1 auto' }} />
                    <Button onClick={handleReset}>Create Another Game</Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Box sx={{ minHeight: 400 }}>
                {getStepContent(activeStep)}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                <Button
                    color="inherit"
                    disabled={activeStep === 0 || loading}
                    onClick={handleBack}
                    sx={{ mr: 1 }}
                >
                    Back
                </Button>
                <Box sx={{ flex: '1 1 auto' }} />

                {activeStep === steps.length - 1 ? (
                    <Button
                        variant="contained"
                        onClick={handleCreateGame}
                        disabled={!isStepValid() || loading}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Create Game'}
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={!isStepValid() || loading}
                    >
                        Next
                    </Button>
                )}
            </Box>
        </Box>
    );
}
